import { Box, TablePagination } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { fetchSubmissionsWithFiltersAPI } from "api/siaApplication";
import { animateRouteProps } from "constants/common";
import { customStylesSelector, selectedAccountSelector } from "store/selectors/selectors";
import { useT } from "translation";
import OutletHeader from "../Common/OutletHeader";
import QuotesFilters from "./QuotesFilters";
import QuotesTable from "./QuotesTable";
import { generateQuotesData } from "./utils";
import { RootState } from "store/store";

const defaultQuotesTableData: QuotesTableData = {
  quotes: [],
  metaData: {
    total: -1,
    offset: -1,
    per_page: -1,
    total_page: -1,
  },
};

const defaultQuotesFilter: FilterParams = {
  page: 1,
  per_page: 5,
  start_date: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
  end_date: dayjs().format("YYYY-MM-DD"),
};

const Quotes = () => {
  const { t } = useT();
  const [page, setPage] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [initialCall, setIntialCall] = useState(true);
  const [loading, setLoading] = useState(false);

  const [quotesData, setQuotesData] = useState<QuotesTableData>(
    defaultQuotesTableData
  );
  const [quotesFilters, setQuotesFilters] =
    useState<FilterParams>(defaultQuotesFilter);
  const customStyles = useSelector(customStylesSelector);
  const selectedAccount = useSelector(selectedAccountSelector);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onDateChange = (newValue: Dayjs, name: string) => {
    setQuotesFilters((f) => ({
      ...f,
      [name]: newValue.format("YYYY-MM-DD"),
    }));
  };

  const onApply = (filterData: Filter[]) => {
    const obj = { ...quotesFilters };
    filterData.forEach((f) => {
      if (f.options.every((o) => o.isSelected)) {
        if (defaultQuotesFilter[f.id]) {
          obj[f.id] = defaultQuotesFilter[f.id];
        } else {
          delete obj[f.id];
        }
      } else {
        obj[f.id] =
          f.options
            .filter((o) => o.isSelected)
            .map((so) => so.value)
            .join(",") ?? "";
      }
    });
    setQuotesFilters(obj);
  };

  const onClear = (filterData: Filter[]) => {
    setQuotesFilters({ ...defaultQuotesFilter });
  };

  const fetchSubmissions = async (payload: FilterParams) => {
    try {
      setLoading(true);
      const response = await fetchSubmissionsWithFiltersAPI(payload);
      if (response?.status === 200 && response?.data?.data?.length) {
        setQuotesData({
          quotes: generateQuotesData(response.data.data),
          metaData: response.data.meta,
        });
      } else {
        setQuotesData(defaultQuotesTableData);
      }
    } catch (error) {
      console.log(error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions({
      ...quotesFilters,
      page: page + 1,
      per_page: rowsPerPage,
      agent_id: selectedAccount?.member?.agent
    });

    if (initialCall) setIntialCall(false);
  }, [rowsPerPage, page, quotesFilters]);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "12px",
      }}
      {...animateRouteProps}
    >
      <OutletHeader
        title={t("QUOTES_PAGE.title")}
        subTitle={t("QUOTES_PAGE.desc")}
      />
      <QuotesFilters
        onDateChange={onDateChange}
        onApply={onApply}
        onClear={onClear}
      />

      <Box sx={{ width: "100%", mt: "20px" }}>
        <Box sx={{ width: "100%", mb: 2 }}>
          <QuotesTable
            loadingData={loading}
            quotesDataList={quotesData.quotes}
            hasError={hasError}
          />
          <TablePagination
            sx={{ color: "black" }}
            labelRowsPerPage={t("TABLES.rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) => {
              return `${from}–${to} ${t("TABLES.of")} ${
                count !== -1 ? count : `${t("TABLES.moreThan")} ${to}`
              }`;
            }}
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={quotesData?.metaData?.total ?? rowsPerPage}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Quotes;
